import { apiFetch, BASE_URL } from "@/lib/auth-api";

/**
 * Harmonogramy powiadomień device-agenta: backend o czasie wysyła komendę
 * `show_notification` do stanowisk aptecznych aptek WŁASNYCH (W-), a helper
 * pokazuje trwały toast w prawym dolnym rogu (do zamknięcia krzyżykiem).
 * camelCase; ścieżki BEZ `/api` (apiFetch dokleja prefix + Bearer).
 */

export type ScheduleType = "once" | "recurring";

export interface NotificationSchedule {
	scheduleId: number;
	name: string;
	title: string;
	/** Treść z minimalnym markdownem: **pogrubienie**. */
	body: string;
	scheduleType: ScheduleType;
	/** once: pełny timestamp odpalenia (ISO, UTC). */
	runAt: string | null;
	/** recurring: dni tygodnia ISO (1=pon..7=niedz). */
	daysOfWeek: number[] | null;
	/** recurring: godziny lokalne Europe/Warsaw, "HH:MM:SS" — może być kilka
	 *  slotów dziennie (np. 09:00 i 16:00). */
	timesOfDay: string[] | null;
	/** recurring: okno obowiązywania, "YYYY-MM-DD", obie daty WŁĄCZNIE.
	 *  null = bez ograniczenia z tej strony. */
	activeFrom: string | null;
	activeTo: string | null;
	/** Okno ważności doręczenia (offline dostaje po włączeniu w tym oknie). */
	validityHours: number;
	enabled: boolean;
	lastFiredAt: string | null;
	/** Obecny = harmonogram ma obrazek; URL podglądu z [`scheduleImageUrl`]. */
	imageToken: string | null;
	/** Prywatny token PDF używany wyłącznie przez uwierzytelnionego agenta. */
	pdfToken: string | null;
	/** Nazwa bieżącego PDF-a do wyświetlenia w panelu. */
	pdfFilename: string | null;
	/** Zakres odbiorców (ScopeSpec string). null = wszystkie stanowiska aptek
	 *  własnych (W-). Np. "wlasne", "region:Mazowsze", "apteka:123". */
	scopeSpec: string | null;
	/** Pomija stanowiska, z których ktoś szukał w Asystencie (test na canary nie). */
	onlyWithoutAsystent: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface ScheduleInput {
	name: string;
	title: string;
	body: string;
	scheduleType: ScheduleType;
	runAt?: string | null;
	daysOfWeek?: number[] | null;
	timesOfDay?: string[] | null;
	/** Legacy pojedyncza godzina — wysyłana razem z `timesOfDay`, żeby backend
	 *  sprzed wielogodzinnych slotów nie odrzucił zapisu w oknie deployu. */
	timeOfDay?: string | null;
	/** Okno obowiązywania harmonogramu cyklicznego, obie daty WŁĄCZNIE. */
	activeFrom?: string | null;
	activeTo?: string | null;
	validityHours?: number;
	enabled?: boolean;
	/** ScopeSpec string; pominięty/null = wszystkie stanowiska aptek własnych. */
	scopeSpec?: string | null;
	onlyWithoutAsystent?: boolean;
}

/** Jedno odpalenie powiadomienia (agregat po urządzeniach). */
export interface NotificationDispatch {
	notificationId: string;
	/** Moment odpalenia (ISO, UTC). */
	firedAt: string;
	title: string;
	/** `schedule` = cron, `schedule_manual` = „Wyślij teraz", `adhoc`,
	 *  `event_rule`, `legacy` = wysyłka sprzed wprowadzenia tego pola. */
	origin: string;
	sourceId: number | null;
	/** Nazwa harmonogramu, gdy wysyłka pochodziła z istniejącego wpisu. */
	sourceName: string | null;
	targeted: number;
	/** Komenda opuściła backend (status `sent` albo `acked`). */
	delivered: number;
	/** Agent potwierdził odbiór komendy — dymek pokazał się na stanowisku.
	 *  To NIE znaczy, że ktoś go przeczytał lub zamknął. */
	acked: number;
	/** Użytkownik zamknął/kliknął dymek (read-receipt z rejestru powiadomień).
	 *  Wysyłki sprzed rejestru i agenty < 0.1.102 dają 0. */
	read: number;
}

/** Jeden odbiorca wysyłki — wiersz drill-downu z rejestru powiadomień. */
export interface NotificationReceipt {
	deviceUuid: string;
	idfirm: number | null;
	pharmacyName: string | null;
	/** Nazwa stanowiska; null gdy urządzenie usunięto. */
	deviceName: string | null;
	sentAt: string;
	/** Ack komendy = dotarło do serwisu agenta; null = jeszcze nie odebrane. */
	deliveredAt: string | null;
	/** Zamknięcie/klik dymka; null = nieodczytane ALBO agent sprzed 0.1.102. */
	readAt: string | null;
}

/** Odbiorcy jednej wysyłki z czasami dostarczenia i odczytania. */
export function listNotificationReceipts(notificationId: string): Promise<NotificationReceipt[]> {
	return apiFetch<NotificationReceipt[]>(
		`/device-agent/notification-dispatches/${encodeURIComponent(notificationId)}/receipts`,
	);
}

export interface DispatchHistory {
	dispatches: NotificationDispatch[];
	/** Odpowiedź przycięta limitem — zaw dopiero po zawężeniu zakresu/filtru. */
	truncated: boolean;
}

/**
 * Historia wysyłek z ostatnich `days` dni (backend przycina do 1..90).
 * `origins` filtruje po źródle PO STRONIE SERWERA — filtr kolumny w tabeli
 * przefiltrowałby tylko to, co zmieściło się w limicie odpowiedzi.
 */
export function listNotificationDispatches(
	days = 30,
	origins?: string[],
): Promise<DispatchHistory> {
	const origin = origins?.length ? `&origin=${encodeURIComponent(origins.join(","))}` : "";
	return apiFetch<DispatchHistory>(`/device-agent/notification-dispatches?days=${days}${origin}`);
}

export interface DispatchResult {
	targeted: number;
	deliveredOnline: number;
}

export function listNotificationSchedules(): Promise<NotificationSchedule[]> {
	return apiFetch<NotificationSchedule[]>("/device-agent/notification-schedules");
}

export function createNotificationSchedule(input: ScheduleInput): Promise<NotificationSchedule> {
	return apiFetch<NotificationSchedule>("/device-agent/notification-schedules", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

export function updateNotificationSchedule(
	scheduleId: number,
	input: ScheduleInput,
): Promise<NotificationSchedule> {
	return apiFetch<NotificationSchedule>(`/device-agent/notification-schedules/${scheduleId}`, {
		method: "PATCH",
		body: JSON.stringify(input),
	});
}

export function deleteNotificationSchedule(scheduleId: number): Promise<void> {
	return apiFetch<void>(`/device-agent/notification-schedules/${scheduleId}`, {
		method: "DELETE",
	});
}

/** Publiczny URL obrazka (ten sam, który załaduje dymek na stanowisku).
 *  BASE_URL już kończy się na `/api`. */
export function scheduleImageUrl(imageToken: string): string {
	return `${BASE_URL}/device-agent/public/notification-image/${imageToken}`;
}

/** Zapisz/zamień obrazek harmonogramu (base64 bez prefixu data:). */
export function setScheduleImage(
	scheduleId: number,
	imageBase64: string,
	contentType: string,
): Promise<{ imageToken: string }> {
	return apiFetch<{ imageToken: string }>(
		`/device-agent/notification-schedules/${scheduleId}/image`,
		{ method: "PUT", body: JSON.stringify({ imageBase64, contentType }) },
	);
}

export function deleteScheduleImage(scheduleId: number): Promise<void> {
	return apiFetch<void>(`/device-agent/notification-schedules/${scheduleId}/image`, {
		method: "DELETE",
	});
}

export function setSchedulePdf(
	scheduleId: number,
	pdfBase64: string,
	filename: string,
): Promise<{ token: string }> {
	return apiFetch<{ token: string }>(`/device-agent/notification-schedules/${scheduleId}/pdf`, {
		method: "PUT",
		body: JSON.stringify({ pdfBase64, filename }),
	});
}

export function deleteSchedulePdf(scheduleId: number): Promise<void> {
	return apiFetch<void>(`/device-agent/notification-schedules/${scheduleId}/pdf`, {
		method: "DELETE",
	});
}

/**
 * Wysyłka „teraz" poza harmonogramem — canary, pełna, albo zawężona do `scope`.
 * `scope` to string ScopeSpec (np. "wlasne", "region:Mazowieckie",
 * "apteka:123") z `encodeScope`; backend rozwiązuje go do aptek własnych i
 * waliduje tenant. Pominięty/undefined = bez zawężenia.
 */
export function sendNotificationNow(
	scheduleId: number,
	opts: { canaryOnly: boolean; scope?: string | null },
): Promise<DispatchResult> {
	return apiFetch<DispatchResult>(`/device-agent/notification-schedules/${scheduleId}/send-now`, {
		method: "POST",
		body: JSON.stringify({
			canaryOnly: opts.canaryOnly,
			...(opts.scope ? { scope: opts.scope } : {}),
		}),
	});
}

// ─── Wysyłka ad-hoc (poza harmonogramem) ──────────────────────────────────────

export interface AdhocSendInput {
	title: string;
	body: string;
	validityHours: number;
	/** Tryb podglądu: tylko urządzenia canary (ignoruje scope). */
	canaryOnly: boolean;
	/** ScopeSpec string (`encodeScope`); brak = wszystkie własne. */
	scope?: string | null;
	/** Token obrazka z [`uploadAdhocImage`]; brak = bez obrazka. */
	imageToken?: string | null;
	/** Token PDF z [`uploadNotificationPdf`]; brak = bez PDF-a. */
	pdfToken?: string | null;
	/** Jedno stanowisko w obrębie `scope` (np. z panelu helpdesku); brak = całe `scope`. */
	deviceUuid?: string | null;
}

/** Wyślij powiadomienie ad-hoc — treść komponowana w dialogu, bez harmonogramu. */
export function sendAdhocNotification(input: AdhocSendInput): Promise<DispatchResult> {
	return apiFetch<DispatchResult>("/device-agent/notifications/send", {
		method: "POST",
		body: JSON.stringify({
			title: input.title,
			body: input.body,
			validityHours: input.validityHours,
			canaryOnly: input.canaryOnly,
			...(input.scope ? { scope: input.scope } : {}),
			...(input.deviceUuid ? { deviceUuid: input.deviceUuid } : {}),
			...(input.imageToken ? { imageToken: input.imageToken } : {}),
			...(input.pdfToken ? { pdfToken: input.pdfToken } : {}),
		}),
	});
}

/** Wgraj efemeryczny obrazek do wysyłki ad-hoc (base64 bez prefixu data:). */
export function uploadAdhocImage(
	imageBase64: string,
	contentType: string,
): Promise<{ imageToken: string }> {
	return apiFetch<{ imageToken: string }>("/device-agent/notifications/image", {
		method: "POST",
		body: JSON.stringify({ imageBase64, contentType }),
	});
}

export function uploadNotificationPdf(
	pdfBase64: string,
	filename: string,
): Promise<{ token: string }> {
	return apiFetch<{ token: string }>("/device-agent/notifications/pdf", {
		method: "POST",
		body: JSON.stringify({ pdfBase64, filename }),
	});
}
